import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config';
import type {
  SamlRequirement,
  SamlSignInInfo,
  SetSamlModeBypassParams,
  UpdateSecuritySamlParams,
  UpdateSecuritySamlResponse,
} from '../../../../types/security';

/**
 * SAML SSO 関連の Callable ラッパ（#2983）。
 * 設定更新 / ログイン時プロバイダ解決 / JIT プロビジョニング / ゲート判定 / 運営バイパス
 */

interface RawResponse<T> {
  success: boolean;
  data: T;
}

/** SAML 設定を保存する（GCIP プロバイダ作成/更新を含む）。companyAdmin / 運営 admin 代行 */
export const updateSecuritySaml = async (
  params: UpdateSecuritySamlParams,
): Promise<UpdateSecuritySamlResponse> => {
  const callable = httpsCallable<UpdateSecuritySamlParams, RawResponse<UpdateSecuritySamlResponse>>(
    functions,
    'updateSecuritySaml',
  );
  const result = await callable(params);
  return result.data.data;
};

/** ログイン画面用: メールのドメインから SAML プロバイダを解決する（未認証で呼べる） */
export const getSamlSignInInfo = async (email: string): Promise<SamlSignInInfo> => {
  const callable = httpsCallable<{ email: string }, RawResponse<SamlSignInInfo>>(
    functions,
    'getSamlSignInInfo',
  );
  const result = await callable({ email });
  return result.data.data;
};

/** SAML ログイン直後の JIT プロビジョニング。アカウントが無ければ自動作成される */
export const completeSamlSignIn = async (): Promise<{ created: boolean }> => {
  const callable = httpsCallable<Record<string, never>, RawResponse<{ created: boolean }>>(
    functions,
    'completeSamlSignIn',
  );
  const result = await callable({});
  return result.data.data;
};

/** SamlEnforcementGate 用: SAML 必須モード / セッション TTL の判定を取得する */
export const getSamlRequirement = async (): Promise<SamlRequirement> => {
  const callable = httpsCallable<Record<string, never>, RawResponse<SamlRequirement>>(
    functions,
    'getSamlRequirement',
  );
  const result = await callable({});
  return result.data.data;
};

/** 運営 admin 専用: SAML 必須モードの一時バイパスを切り替える（ロックアウト救済） */
export const setSamlModeBypass = async (
  params: SetSamlModeBypassParams,
): Promise<{ saml_mode_bypass: boolean }> => {
  const callable = httpsCallable<
    SetSamlModeBypassParams,
    RawResponse<{ saml_mode_bypass: boolean }>
  >(functions, 'setSamlModeBypass');
  const result = await callable(params);
  return result.data.data;
};
